import { FeatureCollection, Point } from 'geojson'
import {
  loadServiceCalibPinImages,
  registerServiceCalibStyleImageMissingHandler,
  resolveStationMapIconIdFromLots,
  SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Source, useMap } from 'react-map-gl'
import { ILot, IStation } from 'utils/interfaces'
import { getBeforeIdUnderRobotMarkers, zoomMapTo } from 'utils/mapUtils'

const STATION_SOURCE_ID = 'all-stations'
const STATION_LAYER_ID = 'all-stations-style'
const STATION_CLUSTER_CIRCLE_ID = 'all-stations-cluster-circle'
const STATION_CLUSTER_COUNT_ID = 'all-stations-cluster-count'

/** Same density as Robot → Service calib map. */
const CLUSTER_MAX_ZOOM = 16
const CLUSTER_RADIUS = 69

const layerStationStyle: any = {
  id: STATION_LAYER_ID,
  type: 'symbol',
  filter: ['!', ['has', 'point_count']],
  layout: {
    'icon-image': ['get', 'iconId'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
    'icon-rotate': ['get', 'yaw'],
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
}

const layerClusterCircle: any = {
  id: STATION_CLUSTER_CIRCLE_ID,
  type: 'circle',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#2563eb',
    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 26, 30, 32],
    'circle-stroke-width': 2,
    'circle-stroke-color': 'rgba(255, 255, 255, 0.45)',
  },
}

const layerClusterCount: any = {
  id: STATION_CLUSTER_COUNT_ID,
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': '#ffffff',
  },
}

const AllStationsOnMap = ({
  map,
  stations,
  lots = [],
  onStationClicked,
  offset,
  autoFitBounds = true,
  interactive = true,
}: {
  map: any | undefined
  stations: IStation[]
  lots?: ILot[]
  onStationClicked: (id?: number) => void
  offset?: number
  autoFitBounds?: boolean
  interactive?: boolean
}) => {
  const maps = useMap()
  const mapFromContext = maps?.current?.getMap?.()
  const mapInstance = mapFromContext ?? map

  const hasZoomedRef = useRef(false)
  const prevAutoFitBoundsRef = useRef(autoFitBounds)
  const [beforeId, setBeforeId] = useState<string | undefined>()

  useEffect(() => {
    if (!mapInstance) {
      setBeforeId(undefined)
      return
    }
    const syncBeforeId = () => {
      const next = getBeforeIdUnderRobotMarkers(mapInstance)
      setBeforeId(prev => (prev === next ? prev : next))
    }
    syncBeforeId()
    mapInstance.on('idle', syncBeforeId)
    mapInstance.on('style.load', syncBeforeId)
    return () => {
      mapInstance.off('idle', syncBeforeId)
      mapInstance.off('style.load', syncBeforeId)
    }
  }, [mapInstance])

  const lotsByStationId = useMemo(() => {
    const grouped = new Map<number, ILot[]>()
    for (const lot of lots) {
      if (lot.robot_station == null) {
        continue
      }
      const stationLots = grouped.get(lot.robot_station) ?? []
      stationLots.push(lot)
      grouped.set(lot.robot_station, stationLots)
    }
    return grouped
  }, [lots])

  useEffect(() => {
    if (autoFitBounds && !prevAutoFitBoundsRef.current) {
      hasZoomedRef.current = false
    }
    prevAutoFitBoundsRef.current = autoFitBounds
  }, [autoFitBounds])

  const stationsOnMap: FeatureCollection<Point> = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: stations
        .filter(station => station.point)
        .map(station => {
          const stationLots = lotsByStationId.get(station.id) ?? []
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: station.point?.coordinates || [],
            },
            properties: {
              opacity: 1,
              id: station.id,
              yaw: 0,
              iconId: resolveStationMapIconIdFromLots(stationLots),
            },
          }
        }),
    }
  }, [stations, lotsByStationId])

  useEffect(() => {
    if (!mapInstance) {
      return
    }
    void loadServiceCalibPinImages(mapInstance)
    const unregister = registerServiceCalibStyleImageMissingHandler(mapInstance)
    const onStyleLoad = () => {
      void loadServiceCalibPinImages(mapInstance)
    }
    mapInstance.on('style.load', onStyleLoad)
    if (mapInstance.isStyleLoaded()) {
      onStyleLoad()
    }
    return () => {
      unregister()
      mapInstance.off('style.load', onStyleLoad)
    }
  }, [mapInstance])

  useEffect(() => {
    if (!autoFitBounds) {
      return
    }
    if (mapInstance && stationsOnMap.features.length > 0 && !hasZoomedRef.current) {
      mapInstance.resize()
      zoomMapTo(mapInstance, stationsOnMap.features, offset ?? 600)
      hasZoomedRef.current = true
    } else if (stationsOnMap.features.length === 0) {
      hasZoomedRef.current = false
    }
  }, [mapInstance, stationsOnMap.features, offset, autoFitBounds])

  const onStationClickedOnMap = useCallback(
    (e: any) => {
      if (e.features?.[0]) {
        onStationClicked(e.features[0].properties.id)
      } else {
        console.warn('Here should be station, but got nothing')
      }
    },
    [onStationClicked]
  )

  const onNothingClicked = useCallback(() => {
    onStationClicked()
  }, [onStationClicked])

  const onClusterClicked = useCallback(
    (e: any) => {
      if (!mapInstance || !e.features?.[0]) {
        return
      }
      const feature = e.features[0]
      const clusterId = feature.properties?.cluster_id
      if (clusterId === undefined) {
        return
      }
      const source = mapInstance.getSource(STATION_SOURCE_ID) as {
        getClusterExpansionZoom?: (clusterId: number, callback: (err: Error | null, zoom: number) => void) => void
      }
      source?.getClusterExpansionZoom?.(clusterId, (err, zoom) => {
        if (err) {
          return
        }
        mapInstance.easeTo({
          center: feature.geometry.coordinates as [number, number],
          zoom,
          duration: 400,
        })
      })
    },
    [mapInstance]
  )

  useEffect(() => {
    if (!interactive || !mapInstance) {
      return
    }
    mapInstance.on('click', onNothingClicked)
    mapInstance.on('click', STATION_LAYER_ID, onStationClickedOnMap)
    mapInstance.on('click', STATION_CLUSTER_CIRCLE_ID, onClusterClicked)

    const setCursorPointer = () => {
      mapInstance.getCanvas().style.cursor = 'pointer'
    }
    const setCursorDefault = () => {
      mapInstance.getCanvas().style.cursor = ''
    }
    mapInstance.on('mouseenter', STATION_LAYER_ID, setCursorPointer)
    mapInstance.on('mouseleave', STATION_LAYER_ID, setCursorDefault)
    mapInstance.on('mouseenter', STATION_CLUSTER_CIRCLE_ID, setCursorPointer)
    mapInstance.on('mouseleave', STATION_CLUSTER_CIRCLE_ID, setCursorDefault)

    return () => {
      mapInstance.off('click', onNothingClicked)
      mapInstance.off('click', STATION_LAYER_ID, onStationClickedOnMap)
      mapInstance.off('click', STATION_CLUSTER_CIRCLE_ID, onClusterClicked)
      mapInstance.off('mouseenter', STATION_LAYER_ID, setCursorPointer)
      mapInstance.off('mouseleave', STATION_LAYER_ID, setCursorDefault)
      mapInstance.off('mouseenter', STATION_CLUSTER_CIRCLE_ID, setCursorPointer)
      mapInstance.off('mouseleave', STATION_CLUSTER_CIRCLE_ID, setCursorDefault)
    }
  }, [mapInstance, interactive, onNothingClicked, onStationClickedOnMap, onClusterClicked])

  return (
    <>
      {stationsOnMap.features.length > 0 && (
        <Source
          id={STATION_SOURCE_ID}
          type="geojson"
          data={stationsOnMap}
          cluster
          clusterMaxZoom={CLUSTER_MAX_ZOOM}
          clusterRadius={CLUSTER_RADIUS}
        >
          <Layer {...layerClusterCircle} source={STATION_SOURCE_ID} beforeId={beforeId} />
          <Layer {...layerClusterCount} source={STATION_SOURCE_ID} beforeId={beforeId} />
          <Layer {...layerStationStyle} source={STATION_SOURCE_ID} beforeId={beforeId} />
        </Source>
      )}
    </>
  )
}

export default AllStationsOnMap
